import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { CaretRightIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/Button';
import { LiveBadge } from '@/components/LiveBadge';
import { EventFeedSkeleton } from './EventFeedSkeleton';
import { cn } from '@/lib/utils';

interface EventFeedContainerProps {
    title: string;
    titleContent?: ReactNode;
    isLive?: boolean;
    isConnected?: boolean;
    isLoading: boolean;
    showViewAll?: boolean;
    onViewAll?: () => void;
    children: ReactNode;
    className?: string;
    hasData?: boolean;
}

export function EventFeedContainer({
    title,
    titleContent,
    isLive = false,
    isConnected,
    isLoading,
    showViewAll = false,
    onViewAll,
    children,
    className,
    hasData: hasDataColumn,
}: EventFeedContainerProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const isDirectoryColumn = typeof hasDataColumn === 'boolean';

    if (isDirectoryColumn && (isLoading || !hasDataColumn)) {
        return null;
    }

    const hasViewAll = showViewAll && onViewAll !== undefined;

    const header = (
        <div className="flex items-center justify-between gap-2 pt-4 pb-2 flex-shrink-0">
            {titleContent ?? (
                <h2 className="text-lg-medium text-white">{title}</h2>
            )}
            {isLive && isConnected && <LiveBadge isConnected={isConnected} />}
        </div>
    );

    const footer = hasViewAll && (
        <div className="flex flex-shrink-0 justify-center pt-2">
            <Button
                variant="ghost"
                size="small"
                onClick={onViewAll}
                trailingIcon={<CaretRightIcon className="size-4" />}
            >
                {t('button.viewAll')}
            </Button>
        </div>
    );

    const body = isDirectoryColumn ? (
        children
    ) : isLoading ? (
        <EventFeedSkeleton />
    ) : (
        children
    );

    const column = (
        <div className="h-full flex flex-col min-h-0">
            {header}
            {body}
            {footer}
        </div>
    );

    return (
        <div
            className={cn(
                'h-full',
                isDirectoryColumn && 'slide-in-right',
                className,
            )}
        >
            {column}
        </div>
    );
}
